import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import Cross from 'aquilla/icons/misc/Cross';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogActions
} from 'aquilla/core/molecules/Dialog';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import { getImage } from '@utils/imageHelper';
import { useRouter } from 'next/router';
import { useContext, useMemo } from 'react';
import { DoubtsAIContext } from '@context/DoubtsAIContext';
import { DOW2_AAD_STARTED } from '@constants/segment';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  CHAT_LOST_TITLE,
  GO_BACK_CONFIRMATION_MESSAGE,
  ASK_NEW_QUESTION_CONFIRMATION_MESSAGE
} from './constants';

const StyledDialog = styled(Dialog)`
  ${mediaQuery.uptoMobile} {
    width: 95%;
  }
`;

const StyledDialogHeader = styled(DialogHeader)`
  padding-bottom: ${Spacings.SPACING_4B};
`;

const StyTypography = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 0%;
  }
`;

const StyledTypographyDesc = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0%;
  }
`;

const StyDialogContent = styled(DialogContent)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyDialogBody = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    padding: 0 10px;
  }
`;

const StyButton = styled(Button)`
  width: 218px;
`;

const ButtonContainer = styled(DialogActions)`
  border-top: 0;
`;

const Image = styled.img`
  ${mediaQuery.uptoMobile} {
    height: 67px;
    width: 67px;
  }
`;

const StyledIconWrapper = styled.div`
  cursor: pointer;
`;

type LeaveChatConfirmationProps = {
  type: 'go back' | 'ask new question';
};

const message: Record<LeaveChatConfirmationProps['type'], string> = {
  'go back': GO_BACK_CONFIRMATION_MESSAGE,
  'ask new question': ASK_NEW_QUESTION_CONFIRMATION_MESSAGE
};

const secondaryButtonLabel: Record<LeaveChatConfirmationProps['type'], string> =
  {
    'go back': 'Go back',
    'ask new question': 'Ask new Question'
  };

const LeaveChatConfirmation = ({
  type,
  goalUid
}: LeaveChatConfirmationProps & { goalUid: string }) => {
  const router = useRouter();

  const {
    setFlowType,
    clearChat,
    setIsGoingBack,
    setIsAskNewQuestion,
    isGoingBack,
    isAskNewQuestion
  } = useContext(DoubtsAIContext);

  const routeGoalSegmentData = useGoalEventProps({ goalUID: goalUid });
  const handleClose = () => {
    if (type === 'go back') {
      setIsGoingBack(false);
    } else if (type === 'ask new question') {
      setIsAskNewQuestion(false);
    }
  };

  const onSecondaryButtonClick = () => {
    if (type === 'go back') {
      const currentDomain = window.location.origin;
      const { referrer } = document;
      if (referrer && referrer.startsWith(currentDomain)) {
        router.back();
      } else {
        router.push('/');
      }
    } else if (type === 'ask new question') {
      segment.track(DOW2_AAD_STARTED, {
        last_primary_source_section: 'Menu Tab',
        click_a_picture_clicked: 'No',
        feature_version: 2,
        doubt_source: 'Existing Doubt',
        doubt_type: 'Doubts AI',
        ...routeGoalSegmentData
      });
      setFlowType('input');
      clearChat();
      handleClose();
    }
  };

  const dialogState = useMemo(
    () => (type === 'go back' ? isGoingBack : isAskNewQuestion),
    [type, isGoingBack, isAskNewQuestion]
  );

  return (
    <StyledDialog open={dialogState} size="small">
      <StyledDialogHeader>
        <StyledIconWrapper onClick={handleClose}>
          <Cross height={24} width={24} />
        </StyledIconWrapper>
      </StyledDialogHeader>
      <StyDialogContent>
        <StyDialogBody>
          <StyTypography variant="h3">{CHAT_LOST_TITLE}</StyTypography>
          <StyledTypographyDesc
            variant="p1"
            color="var(--color-text-secondary)"
          >
            {message[type]}
          </StyledTypographyDesc>
        </StyDialogBody>
        <Image src={getImage('unacademy-ai/confirmation.svg')} alt="" />
      </StyDialogContent>
      <ButtonContainer>
        <StyButton
          label={secondaryButtonLabel[type]}
          variant="secondary"
          size="large"
          onClick={onSecondaryButtonClick}
        />
        <StyButton label="Stay in Chat" size="large" onClick={handleClose} />
      </ButtonContainer>
    </StyledDialog>
  );
};

export default LeaveChatConfirmation;
